import { useEffect, useState, ChangeEventHandler } from 'react';
import { useRecoilState } from 'recoil';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Checkbox,
    FormControl,
    FormControlLabel,
    InputLabel,
    MenuItem,
    Radio,
    RadioGroup,
    Select,
    SelectChangeEvent,
    SxProps,
    TextField,
    Typography,
} from '@mui/material';
import { useAxiosWrapper } from '../helpers/axios-wrapper';
import { PasswordResp, initUser } from '../types.d';
import {
    userListState,
    updateUserState,
    errorState,
    updateSuccessState,
} from '../state/state';


const Update = () => {

    const axiosWrapper = useAxiosWrapper();
    const navigate = useNavigate();
    
    const [userList, setUserList] = useRecoilState(userListState);
    const [updateUser, setUpdateUser] = useRecoilState(updateUserState);
    const [error, setError] = useRecoilState(errorState);
    const [updateSuccess] = useRecoilState(updateSuccessState);
    
    const [showPW, setShowPW] = useState(false);
    const [isAddMode, setIsAddMode] = useState(true);
    const [buttonText, setButtonText] = useState('ADD');
    const [custNoInput, setCustNoInput] = useState('');


    useEffect(() => {
        setUpdateUser({ ...initUser(), newuser: true });
        setError((prev) => ({ ...prev, status: 'N/A', message: 'N/A', isError: false, requestSent: false }));
        setCustNoInput('');

        axiosWrapper.getUserList().then((data) => {
            if (data?.length > 0) {
                setUserList(data);
            }
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Update custNoInput when updateUser.custNo changes (e.g., when loading existing user)
    useEffect(() => {
        if (Array.isArray(updateUser.custNo)) {
            setCustNoInput(updateUser.custNo.join(', '));
        }
    }, [updateUser.custNo]);


    const handleUpdateRadioChange: ChangeEventHandler<HTMLInputElement> = (e) => {
        const isAdd = e.currentTarget.value === 'true';
        setIsAddMode(isAdd);
        setButtonText(isAdd ? 'Add' : 'Update');

        if (isAdd) {
            setUpdateUser({ ...initUser(), newuser: true });
            setCustNoInput('');
        }
    };


    const handleInputChange = (field: keyof typeof updateUser): ChangeEventHandler<HTMLInputElement> => (e) => {
        setUpdateUser({ ...updateUser, [field]: e.target.value });
    };


    const handleUpdateClick = () => {
        // Parse custNoInput into array before submitting
        const custNoArray = custNoInput.split(',').map(n => n.trim()).filter(n => n !== '');
        const userToUpdate = { ...updateUser, custNo: custNoArray };

        axiosWrapper.updateUser(userToUpdate).then((data) => {
            if (data) {
                // Success handled by updateSuccessState
            } else {
                // Error handled by errorState
            }
        });
    };


    const handleDropDownChange = (event: SelectChangeEvent) => {
        setShowPW(false);
        const username = event.target.value as string;
        
        axiosWrapper.getUserInfo(username).then((data) => {
            if (data) {
                setUpdateUser(data);
            }
        });
    };


    const handleCheckBoxClick: ChangeEventHandler<HTMLInputElement> = (e) => {
        const isChecked = e.currentTarget.checked;
        setShowPW(isChecked);
        
        if (isChecked) {
            axiosWrapper.getPW(updateUser.username).then((data) => {
                if (data) {
                    const resp = data as PasswordResp;
                    setUpdateUser({ ...updateUser, password: resp.password });
                }
            });
        } else {
            setUpdateUser({ ...updateUser, password: '' });
        }
    };


    const boxStyle: SxProps = {
        bgcolor: 'background.paper',
        boxShadow: 3,
        borderRadius: 3,
        p: 2,
        fontSize: 26,
        marginBottom: 3,
    };
    
    const textFieldStyle: SxProps = {
        minWidth: '300px',
        maxWidth: '600px',
        width: '70%',
        '@media (max-width: 768px)': {
            width: '100%',
            maxWidth: '100%',
        },
    };


    const handleBackClick = () => {
        navigate('/Home');
    };

    return (
        <Box display="flex"
             flexDirection="column"
             sx={{
                 bgcolor: '#192231',
                 color: 'white',
                 py: 4,
                 px: 4,
                 minHeight: 'calc(100vh - 280px)'
             }}
        >
            <Box flex={1} overflow="auto">
                <Box sx={boxStyle}>
                    <Box sx={{maxWidth: '800px', width: '100%'}}>
                        <Box sx={{
                            ...boxStyle,
                            minWidth: '300px',
                            maxWidth: '600px',
                            width: '70%',
                            '@media (max-width: 768px)': {
                                width: '100%',
                                maxWidth: '100%'
                            }
                        }}>
                            <RadioGroup
                                aria-labelledby="radio"
                                name="radio"
                                value={isAddMode}
                                onChange={handleUpdateRadioChange}
                                className="mb-3"
                                row
                            >
                                <FormControlLabel value={true} control={<Radio/>} label='Add'/>
                                <FormControlLabel value={false} control={<Radio/>} label='Update'/>
                            </RadioGroup>
                        </Box>

                        {!isAddMode &&
                            <FormControl
                                sx={{ ...textFieldStyle, marginBottom: 2 }}
                            >
                                <InputLabel id="select-user-label">Select User</InputLabel>
                                <Select
                                    labelId="select-user-label"
                                    id="selectUser"
                                    label="Select User"
                                    onChange={handleDropDownChange}
                                    value={updateUser.username || ''}
                                >
                                    {userList.map((singleUser) => (
                                        <MenuItem
                                            value={singleUser.username}
                                            id={singleUser.username}
                                            key={singleUser.username}
                                        >
                                            {singleUser.username}
                                        </MenuItem>))
                                    }
                                </Select>
                            </FormControl>
                        }

                        {isAddMode &&
                            <TextField
                                margin="dense"
                                id="username"
                                label="User Name:"
                                variant="outlined"
                                onChange={handleInputChange('username')}
                                required
                                sx={textFieldStyle}
                                className="mb-3"
                                value={updateUser.username}
                            />
                        }

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, marginBottom: 2 }}>
                            <TextField
                                margin="dense"
                                id="password"
                                label="Password:"
                                variant="outlined"
                                onChange={handleInputChange('password')}
                                type={showPW ? 'text' : 'password'}
                                required
                                sx={textFieldStyle}
                                value={updateUser.password}
                            />
                            {!isAddMode &&
                                <Box sx={{ display: 'flex', alignItems: 'center', whiteSpace: 'nowrap' }}>
                                    <Checkbox
                                        sx={{'& .MuiSvgIcon-root': {fontSize: 30}}}
                                        checked={showPW}
                                        onChange={handleCheckBoxClick}
                                    />
                                    <Typography sx={{fontSize: 14, fontWeight: 'bold'}}
                                                gutterBottom>
                                        Show Password?
                                    </Typography>
                                </Box>
                            }
                        </Box>
                        <TextField
                            margin="dense"
                            id="firstname"
                            label="First Name:"
                            variant="outlined"
                            onChange={handleInputChange('firstname')}
                            required
                            sx={textFieldStyle}
                            className="mb-3"
                            value={updateUser.firstname}
                        />

                        <TextField
                            margin="dense"
                            id="lastname"
                            label="Last Name:"
                            variant="outlined"
                            onChange={handleInputChange('lastname')}
                            required
                            sx={textFieldStyle}
                            className="mb-3"
                            value={updateUser.lastname}
                        />

                        <TextField
                            margin="dense"
                            id="email"
                            label="Email:"
                            variant="outlined"
                            onChange={handleInputChange('email')}
                            required
                            sx={textFieldStyle}
                            className="mb-3"
                            value={updateUser.email}
                        />

                        <TextField
                            margin="dense"
                            id="customerno"
                            label="Customer Numbers (comma-separated)"
                            variant="outlined"
                            onChange={(e) => setCustNoInput(e.target.value)}
                            required
                            sx={textFieldStyle}
                            className="mb-3"
                            value={custNoInput}
                            placeholder="e.g., 12345, 67890, 11111"
                            helperText="Enter multiple customer numbers separated by commas"
                        />

                        <Box sx={{display: 'flex', gap: 2, marginTop: 4}}>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleUpdateClick}
                                sx={{
                                    backgroundColor: '#1976d2',
                                    '&:hover': {
                                        backgroundColor: '#1565c0',
                                    },
                                    minWidth: '120px',
                                    height: '48px'
                                }}
                            >
                                {buttonText}
                            </Button>

                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleBackClick}
                                sx={{
                                    backgroundColor: '#757575',
                                    '&:hover': {
                                        backgroundColor: '#616161',
                                    },
                                    minWidth: '120px',
                                    height: '48px'
                                }}
                            >
                                Back
                            </Button>
                        </Box>
                    </Box>
                </Box>

                {error.isError &&
                    <Box sx={{ 
                        backgroundColor: 'rgba(244, 67, 54, 0.1)',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                        borderRadius: 2,
                        padding: 2,
                        marginTop: 3,
                        color: '#f44336',
                        maxWidth: '800px'
                    }}>
                        {error.message}
                    </Box>
                }

                {updateSuccess &&
                    <Box sx={{ 
                        backgroundColor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        borderRadius: 2,
                        padding: 2,
                        marginTop: 3,
                        color: '#4caf50',
                        maxWidth: '800px'
                    }}>
                        Success
                    </Box>
                }
            </Box>
        </Box>);
}

export { Update };